import { formatExhibitTokenBare } from "../../helpers/exhibitNumbering";
import type { ExhibitStyle, TokenType } from "../../types/exhibit_formats";
import { formatExhibitLabel, styleForDepth } from "./exhibitLabels";
import type { ExhibitScheme } from "./types";

export type ExhibitStylePreset = {
  id: string;
  /** Primary labels carry their own example; More Styles show samples. */
  label: string;
  group: "primary" | "more";
  /** Style per depth; past the end the last one is reused. */
  levels: ExhibitStyle[];
};

const level = (
  token: TokenType,
  extra: Partial<ExhibitStyle> = {}
): ExhibitStyle => ({ token, case: "UPPER", punctuation: ".", ...extra });

/**
 * The numbering styles offered wherever a case or the firm picks one: the two
 * primary styles first, then More Styles. A prefix only belongs on the top
 * level (it replaces the punctuation for the whole label), so the prefixed
 * presets fall back to plain numbers underneath.
 */
export const EXHIBIT_STYLE_PRESETS: ExhibitStylePreset[] = [
  {
    id: "letters",
    label: "Letters (A., B., C.)",
    group: "primary",
    /** Folders unlabeled; files under them run A, B, C across the tree. */
    levels: [level("NONE"), level("LETTER")]
  },
  {
    id: "letter",
    label: "Letters Then Numbers (A.1, A.2, B.1)",
    group: "primary",
    levels: [level("LETTER"), level("NUMERIC")]
  },
  {
    id: "numeric",
    label: "Numbers",
    group: "more",
    levels: [level("NUMERIC")]
  },
  {
    id: "numeric-paren",
    label: "Numbers With Parenthesis",
    group: "more",
    levels: [level("NUMERIC", { punctuation: ")" })]
  },
  {
    id: "outline",
    label: "Outline",
    group: "more",
    levels: [level("ROMAN_NUMERAL"), level("LETTER"), level("NUMERIC")]
  },
  {
    id: "exhibit-numeric",
    label: "Exhibit Numbers",
    group: "more",
    levels: [level("NUMERIC", { prefix: "Exhibit" }), level("NUMERIC")]
  },
  {
    id: "exhibit-letter",
    label: "Exhibit Letters",
    group: "more",
    levels: [level("LETTER", { prefix: "Exhibit" }), level("NUMERIC")]
  },
  {
    id: "tab",
    label: "Tabs",
    group: "more",
    levels: [level("NUMERIC", { prefix: "Tab" }), level("NUMERIC")]
  }
];

export const PRIMARY_PRESETS = EXHIBIT_STYLE_PRESETS.filter(
  (p) => p.group === "primary"
);
export const MORE_PRESETS = EXHIBIT_STYLE_PRESETS.filter(
  (p) => p.group === "more"
);

/** The pickers' heading for the presets past the primary two. */
export const MORE_STYLES_LABEL = "More Styles";
/** The picker entry that opens the level builder. */
export const CUSTOM_STYLE_LABEL = "Custom…";

/** Depths compared when matching a scheme to a preset; deeper ones repeat. */
const COMPARED_DEPTHS = 3;

const sameStyle = (a: ExhibitStyle, b: ExhibitStyle) =>
  a.token === b.token &&
  a.case === b.case &&
  a.punctuation === b.punctuation &&
  (a.prefix ?? "") === (b.prefix ?? "");

export const applyPreset = (
  scheme: ExhibitScheme,
  preset: ExhibitStylePreset
): ExhibitScheme => {
  const first = preset.levels.findIndex((s) => s.token !== "NONE");
  return {
    ...scheme,
    default_style: preset.levels[Math.max(first, 0)],
    levels: preset.levels
  };
};

/** The preset the scheme numbers like, or null for a custom scheme. */
export const matchPreset = (scheme: ExhibitScheme): ExhibitStylePreset | null =>
  EXHIBIT_STYLE_PRESETS.find((preset) => {
    const candidate = applyPreset(scheme, preset);
    return Array.from({ length: COMPARED_DEPTHS }, (_, depth) => depth).every(
      (depth) =>
        sameStyle(styleForDepth(scheme, depth), styleForDepth(candidate, depth))
    );
  }) ?? null;

/** "A.", "A.1.", "A.1.1.": the first label at each depth, as the tree shows it. */
export const presetSamples = (
  scheme: ExhibitScheme,
  preset: ExhibitStylePreset,
  depths = COMPARED_DEPTHS
): string[] => {
  const candidate = applyPreset(scheme, preset);
  return Array.from({ length: depths }, (_, depth) => {
    const raw = Array.from({ length: depth + 1 }, (__, d) =>
      formatExhibitTokenBare(candidate.start_at, styleForDepth(candidate, d))
    )
      .filter(Boolean)
      .join(".");
    return formatExhibitLabel(raw, candidate);
  });
};

/** "Letters (A., B., C.)", or "Numbers (1., 1.1.)" with the scheme's samples. */
export const presetName = (
  scheme: ExhibitScheme,
  preset: ExhibitStylePreset
): string =>
  preset.group === "primary"
    ? preset.label
    : `${preset.label} (${presetSamples(scheme, preset, 2).join(", ")})`;

// ---------------------------------------------------------------------------
// Custom schemes: what the level builder can store. The backend numbers each
// level with a token (number, letter, roman) in upper or lower case, or leaves
// it unlabeled (None), joins levels with ".", and reads the prefix and the
// trailing punctuation from the first labeled level.
// ---------------------------------------------------------------------------

export type LevelStyleId = "none" | "A" | "a" | "1" | "I" | "i";

export const NONE_LEVEL: LevelStyleId = "none";
/** What a level below a None one starts with: plain numbers. */
export const DEFAULT_LEVEL: LevelStyleId = "1";

export const LEVEL_STYLES: readonly {
  id: LevelStyleId;
  label: string;
  token: TokenType;
  case: ExhibitStyle["case"];
}[] = [
  { id: "none", label: "None", token: "NONE", case: "UPPER" },
  { id: "A", label: "A, B, C", token: "LETTER", case: "UPPER" },
  { id: "a", label: "a, b, c", token: "LETTER", case: "LOWER" },
  { id: "1", label: "1, 2, 3", token: "NUMERIC", case: "UPPER" },
  { id: "I", label: "I, II, III", token: "ROMAN_NUMERAL", case: "UPPER" },
  { id: "i", label: "i, ii, iii", token: "ROMAN_NUMERAL", case: "LOWER" }
];

/** Levels the builder edits; deeper folders reuse the last one. */
export const MAX_CUSTOM_LEVELS = 4;
export const MAX_PREFIX_CHARS = 20;

export type CustomScheme = {
  levels: LevelStyleId[];
  prefix: string;
  punctuation: ExhibitStyle["punctuation"];
};

export const levelStyleIdOf = (style: ExhibitStyle): LevelStyleId => {
  if (style.token === "NONE") return NONE_LEVEL;
  if (style.token === "NUMERIC") return "1";
  const upper = style.case !== "LOWER";
  if (style.token === "LETTER") return upper ? "A" : "a";
  return upper ? "I" : "i";
};

/** The builder's view of a scheme: one row per configured level. */
export const customSchemeOf = (scheme: ExhibitScheme): CustomScheme => {
  const styles = scheme.levels.length ? scheme.levels : [scheme.default_style];
  const labeled =
    styles.find((s) => s.token !== "NONE") ?? scheme.default_style;
  return {
    levels: styles.slice(0, MAX_CUSTOM_LEVELS).map(levelStyleIdOf),
    prefix: labeled.prefix?.trim() ?? "",
    punctuation: labeled.punctuation === ")" ? ")" : "."
  };
};

/**
 * Why a layout can't be saved, or null. The last level repeats for every
 * deeper folder, so a None there would leave those documents unlabeled; that
 * also rules out every level being None.
 */
export const customSchemeProblem = (custom: CustomScheme): string | null =>
  custom.levels[custom.levels.length - 1] === NONE_LEVEL
    ? "The last level needs a label: it repeats for every deeper folder, so its documents would have none."
    : null;

/**
 * A layout with `depth` set to `id`. Setting the last level to None adds a
 * numbered level beneath it, so "no label on the top folders" leaves the
 * documents in them on 1, 2, 3 without another step.
 */
export const setCustomLevel = (
  custom: CustomScheme,
  depth: number,
  id: LevelStyleId
): CustomScheme => {
  const levels = custom.levels.map((l, i) => (i === depth ? id : l));
  const last = depth === levels.length - 1;
  if (id === NONE_LEVEL && last && levels.length < MAX_CUSTOM_LEVELS)
    levels.push(DEFAULT_LEVEL);
  return { ...custom, levels };
};

/** "Level 1 folders get no label. …": what the preview's None levels mean. */
export const unlabeledNote = (custom: CustomScheme): string | null => {
  const levels = custom.levels
    .map((id, depth) => (id === NONE_LEVEL ? depth + 1 : null))
    .filter((n): n is number => n !== null);
  if (!levels.length) return null;
  const names =
    levels.length === 1
      ? `Level ${levels[0]}`
      : `Levels ${levels.slice(0, -1).join(", ")} and ${
          levels[levels.length - 1]
        }`;
  return `${names} folders get no label. The documents in them number on across folders, so no two share a label.`;
};

/** The scheme a custom layout saves, keeping the scheme's start and citations. */
export const applyCustom = (
  scheme: ExhibitScheme,
  custom: CustomScheme
): ExhibitScheme => {
  const prefix = custom.prefix.trim().slice(0, MAX_PREFIX_CHARS);
  const first = custom.levels.findIndex((id) => id !== NONE_LEVEL);
  const levels = custom.levels.map((id, depth): ExhibitStyle => {
    const style = LEVEL_STYLES.find((s) => s.id === id) ?? LEVEL_STYLES[3];
    return {
      token: style.token,
      case: style.case,
      punctuation: depth === first ? custom.punctuation : ".",
      ...(depth === first && prefix ? { prefix } : {})
    };
  });
  return {
    ...scheme,
    default_style: levels[first] ?? levels[0],
    levels
  };
};
